import { BreakpointObserver, type BreakpointState } from '@angular/cdk/layout';
import { ViewportRuler } from '@angular/cdk/overlay';
import { inject, Injectable, Injector, signal, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, merge, of, fromEvent } from 'rxjs';
import { MediaBreakpointsEnum } from '../enums/media-breakpoints.enum';
import { type ElementSize } from '../models/element-size.model';
import { PlatformService } from './platform.service';

@Injectable({
    providedIn: 'root',
})
export class BreakpointService {
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly injector = inject(Injector);
    private readonly viewportRuler = inject(ViewportRuler);
    private readonly platformService = inject(PlatformService);
    public readonly isMobile = this.formatResult(MediaBreakpointsEnum.mdMediaDown);
    public readonly isTablet = this.formatResult(MediaBreakpointsEnum.mdMediaOnly);
    public readonly isDesktop = this.formatResult(MediaBreakpointsEnum.lgMediaUp);
    public readonly isRetina = this.formatResult(MediaBreakpointsEnum.retina);

    public readonly topBarSize = signal<ElementSize>({
        width: 0,
        height: 0,
    });

    public readonly headerSize = signal<ElementSize>({
        width: 0,
        height: 0,
    });

    public readonly viewPortSize = toSignal(
        // we use of here because the viewport size should be updated immediately on the client side not only on resize
        // this is to prevent a flickering effect on the page
        // eslint-disable-next-line no-null/no-null
        merge(of(null), this.viewportRuler.change()).pipe(
            map(() => this.viewportRuler.getViewportSize() as ElementSize),
            distinctUntilChanged((a, b) => a.width === b.width && a.height === b.height),
        ),
        { initialValue: this.viewportRuler.getViewportSize() as ElementSize },
    );

    public readonly scrollPositionY = toSignal(
        // Emit immediately on the client and subscribe to viewport changes (scroll/resize)
        // Also listen to native window scroll to cover cases where CDK doesn't emit for scroll
        merge(
            // eslint-disable-next-line no-null/no-null
            of(null),
            this.viewportRuler.change(),
            // Guard against SSR: only access window in the browser
            ...(this.platformService.isPlatformServer() ? [] : [fromEvent(window, 'scroll', { passive: true })]),
        ).pipe(
            map(() => this.viewportRuler.getViewportScrollPosition().top),
            distinctUntilChanged(),
        ),
        { initialValue: this.viewportRuler.getViewportScrollPosition().top },
    );

    private formatResult(breakpoint: string): Signal<boolean> {
        // Use isMatched() for the initial value to avoid a wrong first render on the client.
        // Without this, we would start with `false` and update a moment later, which can cause
        // visible flickering in responsive components that depend on these signals.
        const initial = this.breakpointObserver.isMatched(breakpoint);

        return toSignal(
            this.breakpointObserver
                .observe(breakpoint)
                .pipe(map((result: BreakpointState): boolean => result.matches)),
            {
                initialValue: initial,
                injector: this.injector,
            },
        );
    }
}
